import { useCallback, useState, type FunctionComponent } from 'react'
import { InternalLink } from '../../const/Links'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ProductViewResult } from '../../data/ProductFragment'
import { ProductPreview } from '../product/ProductPreview'
import { CardSwiper } from '../swiper/CardSwiper'
import { Button } from './Button'
import { Hidden } from './Hidden'
import { LoaderCarousel } from './LoaderCarousel'
import { LoginModal } from './LoginModal'
import style from './ProductCarousel.module.sass'
import { Title } from './Title'

type ProductCarouselProps = {
	ingredientDeclension?: string
	products: ProductViewResult[]
	title?: string
	loading?: boolean
	buttonLink?: string
	productAmount?: number
}

export const ProductCarousel: FunctionComponent<ProductCarouselProps> = ({
	ingredientDeclension,
	products,
	loading = false,
	title,
	buttonLink,
	productAmount,
}) => {
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
	const translate = useTranslate()

	const ingredientDeclensionArray = ingredientDeclension?.split(' ')
	const isNegative = ingredientDeclensionArray && ingredientDeclensionArray[0].startsWith('ne')

	const toggleLoginModal = useCallback((value: boolean) => setIsLoginModalOpen(value), [])

	return (
		<>
			{products.length > 0 && !loading && (
				<>
					{isLoginModalOpen && <LoginModal closeModal={() => toggleLoginModal(false)} isModalOpen={isLoginModalOpen} />}
					<Title
						title={
							title ?? (
								<>
									{ingredientDeclension && ingredientDeclensionArray ? (
										<>
											<Translation tr="productCarousel.title.declension" />{' '}
											<span className={isNegative ? style.greenColor : ''}>{ingredientDeclensionArray[0]} </span>
											<span className={style.greenColor}>{ingredientDeclensionArray?.splice(1)?.join(' ')}</span>
										</>
									) : (
										<>
											<span className={isNegative ? style.greenColor : ''}>
												<Translation tr="productCarousel.title.item.first" />
											</span>
											<span className={style.greenColor}>
												<Translation tr="productCarousel.title.item.second" />
											</span>
										</>
									)}
								</>
							)
						}
						hasButton={Boolean(buttonLink)}
						buttonLabel={
							productAmount
								? `${translate('ingredient.carousel.button.label')} (${productAmount})`
								: translate('productCarousel.button.moreResults')
						}
						buttonUrl={buttonLink}
					/>
					<Hidden breakpoint="mobileDown" isGridVisible>
						<CardSwiper>
							{products.map((product) => (
								<ProductPreview
									key={product.id}
									title={
										product.productLine
											? `${product.brand?.name} ${product.productLine?.name}`
											: `${product.brand?.name}`
									}
									url={`${InternalLink.Product}${product.slug}`}
									name={product.name}
									openModal={() => toggleLoginModal(true)}
									imageUrl={product.heurekaImage?.url ?? product?.image?.url ?? '/images/placeholder-product.png'}
									id={product.id}
								/>
							))}
						</CardSwiper>
					</Hidden>
					<Hidden breakpoint="mobileUp">
						<div className={style.mobileGrid}>
							{loading ? (
								<LoaderCarousel amount={4} />
							) : (
								products
									.map((product) => (
										<ProductPreview
											key={product.id}
											openModal={() => toggleLoginModal(true)}
											title={
												product.productLine
													? `${product.brand?.name} ${product.productLine?.name}`
													: `${product.brand?.name}`
											}
											name={product.name}
											id={product.id}
											url={`${InternalLink.Product}${product.slug}`}
											imageUrl={product.heurekaImage?.url ?? product?.image?.url ?? '/images/placeholder-product.png'}
										/>
									))
									.slice(0, buttonLink ? 4 : 8)
							)}
						</div>
						<div className={style.buttonWrapper}>
							{buttonLink && (
								<Button
									label={
										productAmount
											? `${translate('ingredient.carousel.button.label')} (${productAmount})`
											: translate('productCarousel.button.moreResults')
									}
									type={{ variant: 'underlinedGrey' }}
									url={buttonLink}
								/>
							)}
						</div>
					</Hidden>
				</>
			)}
		</>
	)
}
